import { useMutation } from '@tanstack/react-query';
import { api } from '@app/utils/api';
import { errorHandler } from '@app/utils/errorHandler';
import { format } from 'date-fns';
import { BI_BASE_URL } from '@app/contstants/endpoints';
import { getDatesBetweenDates, isEqualTwoDates } from '@app/utils/date';
import { IAccountLine } from '@entities/account/IAccountLine';

export const fetch = async (filters: {
  dateFrom?: string;
  dateTo?: string;
  account: string;
}): Promise<IAccountLine[]> => {
  let params = {};

  if (filters.dateFrom) {
    params = {
      ...params,
      date_from: format(new Date(filters.dateFrom), 'yyyy-MM-dd'),
    };
  }

  if (filters.dateTo) {
    params = {
      ...params,
      date_to: format(new Date(filters.dateTo), 'yyyy-MM-dd'),
    };
  }

  const response = await api.get<IAccountLine[]>(
    `${BI_BASE_URL}/accounts/${filters.account}/line`,
    { params }
  );
  const data = response.data ?? [];

  if (!data.length) {
    return [];
  }

  const firstItem = response.data[0];

  if (filters.dateFrom) {
    const start = new Date(filters.dateFrom);
    const end = new Date(firstItem.date);

    if (!isEqualTwoDates(start, end)) {
      const missedDates = getDatesBetweenDates(start, end);

      const missedValues = missedDates.map(missedDate => ({
        date:
          missedDate.getFullYear() +
          '-' +
          (missedDate.getMonth() + 1).toString().padStart(2, '0') +
          '-' +
          missedDate.getDate().toString().padStart(2, '0'),
        hashrate: 0,
        referral_hashrate: 0,
      }));

      return [...missedValues, ...data];
    }
  }

  return response.data ?? [];
};

export const useAccountLine = () => {
  const { mutateAsync, isError, isPending, isSuccess, error } = useMutation({
    mutationFn: fetch,
    onError: error => errorHandler(error),
  });

  return {
    fetch: mutateAsync,
    isError,
    isPending,
    isSuccess,
    error,
  };
};
